import React from 'react';
import Button from '../../../shared/components/Button';
import Card from '../../../shared/components/Card';
import ServerStatus from '../../../shared/components/ServerStatus';
import { useLanguage } from '../../../shared/contexts/LanguageContext';
import useTranslation from '../../../shared/hooks/useTranslation';

interface HeroSectionProps {
  onLogin: () => void;
  onSignup: () => void;
  /** Masque l’inscription (ex. maintenance : seuls les CM peuvent se connecter). */
  maintenanceMode?: boolean;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onLogin, onSignup, maintenanceMode }) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  return (
    <section className="hero py-20 pt-40 relative overflow-hidden z-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="text-center">
          <div className="mb-8 animate-fade-in">
            <div className="inline-block glow mb-6">
              <img 
                src="/images/logo.png" 
                alt="OpSecours" 
                className="w-32 h-32 mx-auto drop-shadow-2xl" 
              />
            </div>
            <h1 className="text-7xl md:text-8xl font-black mb-4">
              <span className="text-gradient block">
                {t('hero.title')}
              </span>
            </h1>
          </div>
          
          <p className="text-2xl md:text-3xl text-gray-300 mb-12 max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
            {t('hero.subtitle')}
          </p>

          <p className="text-xl text-gray-400 max-w-5xl mx-auto mb-12 leading-relaxed animate-fade-in" style={{ animationDelay: '0.3s' }}>
            {t('hero.description')}
          </p>

          <div className="flex flex-row items-center justify-center gap-6 mb-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <Card hover className="bg-red-900/50 border-red-600/50 px-6 py-3">
              <div className="flex items-center text-red-200">
                <img src="/images/alert.png" alt={t('hero.badges.realtime')} className="w-6 h-6 mr-3" />
                <span className="text-lg font-semibold">{t('hero.badges.realtime')}</span>
              </div>
            </Card>
            <Card hover className="bg-blue-900/50 border-blue-600/50 px-6 py-3">
              <div className="flex items-center text-blue-200">
                <img src="/images/icon-cta.png" alt={t('hero.badges.calls')} className="w-6 h-6 mr-3" />
                <span className="text-lg font-semibold">{t('hero.badges.calls')}</span>
              </div>
            </Card>
            <Card hover className="bg-green-900/50 border-green-600/50 px-6 py-3">
              <div className="flex items-center text-green-200">
                <img src="/images/argent_jeu.png" alt={t('hero.badges.bonus')} className="w-6 h-6 mr-3" />
                <span className="text-lg font-semibold">{t('hero.badges.bonus')}</span>
              </div>
            </Card>
          </div>

          <div className="flex flex-row gap-6 justify-center mb-12 animate-fade-in" style={{ animationDelay: '0.5s' }}>
            <Button variant="primary" size="lg" onClick={onLogin} className="min-w-[200px]">
              <span className="flex items-center gap-2">
                <img src="/images/icons8-homme-debout-50.png" alt={t('hero.buttons.login')} className="w-6 h-6" />
                {t('hero.buttons.login')}
              </span>
            </Button>
            {!maintenanceMode && (
              <Button variant="secondary" size="lg" onClick={onSignup} className="min-w-[200px]">
                <span className="flex items-center gap-2">
                  <img src="/images/icons8-courir-60.png" alt={t('hero.buttons.signup')} className="w-6 h-6" />
                  {t('hero.buttons.signup')}
                </span>
              </Button>
            )}
          </div>

          {/* Statut du Serveur */}
          <div className="mt-12 animate-fade-in" style={{ animationDelay: '0.6s' }}>
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {t('serverStatus.title') || 'Statut du Serveur'}
              </h3>
              <p className="text-gray-400 text-sm">
                {t('serverStatus.description') || 'Vérifiez l\'état du serveur'}
              </p>
            </div>
            <div className="max-w-md mx-auto">
              <ServerStatus />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;

